import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useObs } from '../../contexts/ObsContext';
import { useAuth } from '../../contexts/AuthContext';
import { ProgressBar, Hint, Btn, SecLabel } from '../../components/ui';
import { GRADE_OPTIONS, SUBJECT_OPTIONS } from '../../theme';
import { obsAPI } from '../../api';
import CoObservation from '../../components/CoObservation';

const STEPS = ['Context', 'Evidence', 'Summary', 'Feedback'];

const today = () => new Date().toISOString().slice(0, 10);

/**
 * District and school come from the account, not from a choice made at
 * observation time. Only a Whitehead Ross consultant works across districts.
 */
function scopeFor(roleCode) {
  switch (roleCode) {
    case 'consultant':
    case 'administrator':
      return {
        districtLocked: false,
        schoolLocked: false,
        blurb: 'You work across districts, so district is selectable for your account type.',
      };
    case 'district':
      return {
        districtLocked: true,
        schoolLocked: false,
        districtReason: 'Your district is set by your account.',
        blurb: 'Your district is set by your account. You can observe in any school within it.',
      };
    default: // teacher, middle_leader, school_leader
      return {
        districtLocked: true,
        schoolLocked: true,
        districtReason: 'Your district is set by your account.',
        schoolReason: 'Your school is set by your account.',
        blurb: 'Your district and school are set by your account, so both are fixed here.',
      };
  }
}

function LockNote({ children }) {
  return (
    <div className="lock-note">
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <rect x="2.5" y="5.2" width="7" height="5.3" rx="1.2" stroke="currentColor" strokeWidth="1.1" />
        <path d="M4.2 5.2V3.9a1.8 1.8 0 0 1 3.6 0v1.3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
      </svg>
      <span>{children}</span>
    </div>
  );
}

export default function ContextPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setObs, setTeacher, resetObs } = useObs();

  const scope = useMemo(() => scopeFor(user?.role_code), [user?.role_code]);

  const [districts, setDistricts] = useState([]);
  const [schools, setSchools] = useState([]);
  const [teachers, setTeachers] = useState([]);

  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('');
  const [teacher, setTch] = useState(null);

  const [obsDate, setObsDate] = useState(today());
  // Any observation can become a co-observation. The code is reserved when
  // that is chosen, and dropped again if the observer decides to work alone.
  const [coMode, setCoMode] = useState('alone');
  const [sessionCode, setSessionCode] = useState('');

  const changeCoMode = useCallback((next) => {
    setCoMode(next);
    if (next !== 'lead') setSessionCode('');
  }, []);
  const [grade, setGrade] = useState('');
  const [subject, setSubject] = useState('Mathematics');
  const [aim, setAim] = useState('');

  const [loadingDistricts, setLoadingDistricts] = useState(true);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Districts the account may observe in. Locked accounts get exactly one back,
  // so it can be selected for them.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await obsAPI.getDistricts();
        const list = data.results ?? data;
        if (cancelled) return;
        setDistricts(list);
        setSelectedDistrict(user.district || (list.length === 1 ? list[0].id : ''));
      } catch (err) {
        console.error('Error fetching districts:', err);
      } finally {
        if (!cancelled) setLoadingDistricts(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Selecting a district filters the schools.
  useEffect(() => {
    if (!user) return;
    if (!selectedDistrict) { setSchools([]); return; }
    let cancelled = false;
    setLoadingSchools(true);
    (async () => {
      try {
        const { data } = await obsAPI.getSchools(selectedDistrict);
        const list = data.results ?? data;
        if (cancelled) return;
        setSchools(list);
        // A locked account lands on its own school with the list already populated.
        const own = user.school_id;
        if (own && list.some(s => s.id === own)) setSelectedSchool(own);
        else if (list.length === 1 && scope.schoolLocked) setSelectedSchool(list[0].id);
        else setSelectedSchool(prev => (list.some(s => s.id === prev) ? prev : ''));
      } catch (err) {
        console.error('Error fetching schools:', err);
      } finally {
        if (!cancelled) setLoadingSchools(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDistrict, user, scope.schoolLocked]);

  // Selecting a school filters the teacher list to that school's staff only.
  useEffect(() => {
    if (!selectedSchool) { setTeachers([]); setTch(null); return; }
    let cancelled = false;
    setLoadingTeachers(true);
    (async () => {
      try {
        const { data } = await obsAPI.getTeachers(selectedSchool);
        if (cancelled) return;
        setTeachers(data.results ?? data);
        setTch(null);
      } catch (err) {
        console.error('Error fetching teachers:', err);
      } finally {
        if (!cancelled) setLoadingTeachers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSchool]);

  /** Joining someone else's session skips straight to recording — the lead
   *  has already set the context for the lesson. */
  const handleJoined = (observation) => {
    resetObs();
    setObs(observation);
    setTeacher(observation.teacher_detail || null);
    navigate('/observe/evidence');
  };

  const handleStart = async () => {
    if (!selectedSchool || !grade || !subject || !teacher || !obsDate) {
      setError('Before you can start, please choose a school, the teacher you are '
               + 'observing, a grade level and a subject.');
      return;
    }
    setError('');
    setLoading(true);
    resetObs();
    try {
      const { data } = await obsAPI.start({
        school: selectedSchool,
        teacher: teacher.id,
        observation_date: obsDate,
        grade_level: grade,
        subject,
        lesson_aim: aim,
        // Every observation has a code, but only a co-observation shares one.
        is_co_observation: coMode === 'lead',
        // Created with the code the lead has already shared, not a new one.
        ...(coMode === 'lead' && sessionCode ? { session_code: sessionCode } : {}),
      });
      setObs(data);
      setTeacher(teacher);
      navigate('/observe/evidence');
    } catch (err) {
      const detail = err?.response?.data;
      setError(
        (detail && (detail.school?.[0] || detail.detail)) ||
        'Could not start the observation. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="screen">
      <div className="obsbar">
        <button className="obsbar-back" onClick={() => navigate('/')}>← Back</button>
        <div className="obsbar-title">New observation</div>
      </div>
      <ProgressBar steps={STEPS} current={0} />

      <div className="scroll">
        <div className="pad">
          <div className="scope-note">
            <strong>Scope.</strong> {scope.blurb}
          </div>

          <SecLabel style={{ marginTop: 0 }}>The context of the lesson</SecLabel>

          {error && (
            <div style={{ background:'var(--red-l)', borderRadius:'var(--rs)', padding:'8px 12px', marginBottom:12, fontSize:12, color:'var(--red-d)' }}>
              {error}
            </div>
          )}

          <div className="field-gap">
            <div className="field-lbl">District</div>
            <select
              className="fi"
              value={selectedDistrict}
              disabled={scope.districtLocked || loadingDistricts}
              onChange={e => setSelectedDistrict(e.target.value)}
            >
              <option value="">{loadingDistricts ? 'Loading districts…' : 'Select district…'}</option>
              {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            {scope.districtLocked && !loadingDistricts && (
              <LockNote>{scope.districtReason}</LockNote>
            )}
          </div>

          <div className="field-gap">
            <div className="field-lbl">School</div>
            <select
              className="fi"
              value={selectedSchool}
              disabled={scope.schoolLocked || !selectedDistrict || loadingSchools}
              onChange={e => setSelectedSchool(e.target.value)}
            >
              <option value="">
                {loadingSchools ? 'Loading schools…'
                  : !selectedDistrict ? 'Select a district first…'
                  : 'Select school…'}
              </option>
              {schools.map(s => <option key={s.id} value={s.id}>{s.dropdown_label || s.name}</option>)}
            </select>
            {scope.schoolLocked && !loadingSchools && (
              <LockNote>{scope.schoolReason}</LockNote>
            )}
          </div>

          <div className="field-gap">
            <div className="field-lbl">Teacher observed</div>
            <select
              className="fi"
              value={teacher?.id || ''}
              disabled={!selectedSchool || loadingTeachers}
              onChange={e => setTch(teachers.find(t => t.id === e.target.value) || null)}
            >
              <option value="">
                {loadingTeachers ? 'Loading teachers…'
                  : !selectedSchool ? 'Select a school first…'
                  : teachers.length === 0 ? 'No staff listed for this school'
                  : 'Select teacher…'}
              </option>
              {teachers.map(t => <option key={t.id} value={t.id}>{t.dropdown_label || t.full_name}</option>)}
            </select>
            <div className="lock-note" style={{ color: 'var(--text3)' }}>
              <span>Shows only staff at the selected school.</span>
            </div>
          </div>

          <div className="field-gap">
            <div className="field-lbl">Date of observation</div>
            <input
              className="fi"
              type="date"
              value={obsDate}
              max={today()}
              onChange={e => setObsDate(e.target.value)}
            />
            <div className="lock-note" style={{ color: 'var(--text3)' }}>
              <span>Defaults to today. Change it if you are recording an earlier observation.</span>
            </div>
          </div>

          <div className="field-gap">
            <div className="field-lbl">Grade level</div>
            <select className="fi" value={grade} onChange={e => setGrade(e.target.value)}>
              <option value="">Select grade…</option>
              {GRADE_OPTIONS.map(g => <option key={g}>{g}</option>)}
            </select>
          </div>

          <div className="field-gap">
            <div className="field-lbl">Subject area</div>
            <select className="fi" value={subject} onChange={e => setSubject(e.target.value)}>
              {SUBJECT_OPTIONS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>

          <div className="field-gap">
            <div className="field-lbl">Observer</div>
            <input className="fi filled" value={user?.full_name || ''} readOnly />
          </div>

          <div className="field-gap">
            <div className="field-lbl">What is the lesson aiming to achieve? (optional)</div>
            <textarea className="fi" rows={3} value={aim} onChange={e => setAim(e.target.value)} placeholder="Describe the intended learning for this lesson…" />
          </div>

          <SecLabel>Co-observation</SecLabel>
          <CoObservation
            mode={coMode}
            onModeChange={changeCoMode}
            sessionCode={sessionCode}
            onCodeReserved={setSessionCode}
            onJoined={handleJoined}
          />

          {coMode !== 'joining' && (
            <Btn variant="blue" onClick={handleStart} loading={loading}>Start observation →</Btn>
          )}
        </div>
      </div>
    </div>
  );
}
